import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Loader2, Upload, X } from 'lucide-react';
import { CE_QU_EST_LE_TYPE, NOM_DU_TYPE_PLURIEL, type TypeDActivite } from '../../../../lib/seo/stage';
import { ACCEPT_IMAGE, ImageRefusee, preparerImage } from '../../../../lib/stages/imagesDeStage';
import { lignesDeMateriel, texteDeMateriel } from '../../../../lib/stages/materiel';
import {
  EchecDEnregistrement,
  useStageStore,
  type ChampsDuStage,
  type Stage,
} from '../../../../stores/stageStore';

interface Props {
  /** Le stage a modifier ; absent pour en creer un. */
  stage: Stage | null;
  onClose: () => void;
  /** Appele une fois la fiche (et sa photo) enregistree. */
  onSaved: (stage: Stage, cree: boolean) => void;
}

interface Formulaire {
  title: string;
  description: string;
  age_min: string;
  age_max: string;
  /*
    Le defaut de la base est « stage ». On le reprend ici pour que le
    formulaire dise la meme chose que la colonne — un champ vide laisserait
    croire qu'aucun choix n'est fait, alors qu'il en est fait un.
  */
  type_activite: TypeDActivite;
  active: boolean;
  /** Une ligne par élément ; nettoyé à l'enregistrement. */
  materiel: string;
  pret_materiel: boolean;
}

const formulaireDe = (stage: Stage | null): Formulaire => ({
  title: stage?.title ?? '',
  description: stage?.description ?? '',
  age_min: stage ? String(stage.age_min) : '',
  age_max: stage ? String(stage.age_max) : '',
  type_activite: (stage?.type_activite ?? 'stage') as TypeDActivite,
  active: stage ? stage.active !== false : true,
  materiel: texteDeMateriel(stage?.materiel_a_prevoir),
  pret_materiel: stage?.pret_materiel_possible === true,
});

const LONGUEUR_CONSEILLEE = 600;

function erreursDe(f: Formulaire): Record<string, string> {
  const erreurs: Record<string, string> = {};
  const min = parseFloat(f.age_min);
  const max = parseFloat(f.age_max);
  if (!f.title.trim()) erreurs.title = 'Le titre est obligatoire.';
  if (!f.description.trim()) erreurs.description = 'La description est obligatoire.';
  if (isNaN(min) || min < 2 || min > 18) erreurs.age_min = 'Entre 2 et 18 ans.';
  if (isNaN(max) || max < 2 || max > 19) erreurs.age_max = 'Entre 2 et 19 ans.';
  else if (!isNaN(min) && max <= min) erreurs.age_max = 'Doit être supérieur à l’âge minimum.';
  return erreurs;
}

const messageDe = (debut: string, detail: string) => `${debut}${detail ? ` (${detail})` : ''}. Réessayez.`;

const StageFormModal = ({ stage: stageInitial, onClose, onSaved }: Props) => {
  const { saveStage, enregistrement } = useStageStore();

  /*
    Le stage en cours d'edition peut changer sans que la fenetre se ferme :
    quand la creation reussit mais que la photo echoue, la fenetre bascule en
    modification sur le stage cree, pour qu'un nouvel essai ne le duplique pas.
  */
  const [stage, setStage] = useState<Stage | null>(stageInitial);
  const [initial] = useState(() => formulaireDe(stageInitial));
  const [form, setForm] = useState<Formulaire>(initial);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [apercu, setApercu] = useState<string | null>(stageInitial?.image_url ?? null);
  /*
    La compression prend une ou deux secondes. Tant qu'elle court, la photo
    n'est pas encore prete : enregistrer a ce moment-la sauvait le stage sans
    elle, sans rien dire. Le bouton attend donc la fin du traitement.
  */
  const [preparation, setPreparation] = useState(false);
  const [erreurImage, setErreurImage] = useState<string | null>(null);
  const [erreurEnregistrement, setErreurEnregistrement] = useState<string | null>(null);
  const [confirmerFermeture, setConfirmerFermeture] = useState(false);

  const titreRef = useRef<HTMLInputElement>(null);
  useEffect(() => titreRef.current?.focus(), []);

  // L'apercu local est une adresse `blob:` : la liberer des qu'elle est remplacee.
  useEffect(
    () => () => {
      if (apercu?.startsWith('blob:')) URL.revokeObjectURL(apercu);
    },
    [apercu]
  );

  const modifie = useMemo(
    () => imageFile !== null || (Object.keys(initial) as (keyof Formulaire)[]).some((k) => form[k] !== initial[k]),
    [form, initial, imageFile]
  );
  const occupe = enregistrement || preparation;

  // Fermer sans perdre en silence ce qui a ete saisi.
  const demanderFermeture = () => {
    if (occupe) return;
    if (modifie) setConfirmerFermeture(true);
    else onClose();
  };

  useEffect(() => {
    const surTouche = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (confirmerFermeture) setConfirmerFermeture(false);
      else demanderFermeture();
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  });

  const changer = <K extends keyof Formulaire>(champ: K, valeur: Formulaire[K]) => {
    setForm((f) => ({ ...f, [champ]: valeur }));
    setConfirmerFermeture(false);
    if (erreurs[champ]) {
      setErreurs((actuelles) => {
        const reste = { ...actuelles };
        delete reste[champ];
        return reste;
      });
    }
  };

  const choisirImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Vider le champ permet de rechoisir le meme fichier apres une erreur.
    event.target.value = '';
    if (!file) return;

    setErreurImage(null);
    setPreparation(true);
    try {
      const prete = await preparerImage(file);
      setImageFile(prete);
      setApercu(URL.createObjectURL(prete));
    } catch (error) {
      setErreurImage(error instanceof ImageRefusee ? error.message : 'Impossible de traiter cette image.');
    } finally {
      setPreparation(false);
    }
  };

  const enregistrer = async (e: React.FormEvent) => {
    e.preventDefault();
    const trouvees = erreursDe(form);
    setErreurs(trouvees);
    if (Object.keys(trouvees).length > 0) return;

    setErreurImage(null);
    setErreurEnregistrement(null);

    const champs: ChampsDuStage = {
      title: form.title.trim(),
      description: form.description.trim(),
      age_min: parseFloat(form.age_min),
      age_max: parseFloat(form.age_max),
      type_activite: form.type_activite,
      active: form.active,
      materiel_a_prevoir: lignesDeMateriel(form.materiel),
      // Un prêt sans matériel noté n'aurait rien à proposer.
      pret_materiel_possible: form.pret_materiel && lignesDeMateriel(form.materiel).length > 0,
    };

    try {
      const enregistre = await saveStage({ id: stage?.id, champs, image: imageFile });
      onSaved(enregistre, !stageInitial);
    } catch (error) {
      console.error('Error saving stage:', error);
      if (!(error instanceof EchecDEnregistrement)) {
        setErreurEnregistrement(messageDe('Le stage n’a pas pu être enregistré', ''));
      } else if (error.etape === 'photo') {
        if (error.stageCree) {
          setStage(error.stageCree);
          setErreurImage(
            messageDe('Le stage a été créé, mais la photo n’a pas pu être envoyée', error.message)
          );
        } else {
          setErreurImage(messageDe('La photo n’a pas pu être envoyée', error.message));
        }
      } else {
        setErreurEnregistrement(
          messageDe(stage ? 'Le stage n’a pas pu être enregistré' : 'Le stage n’a pas pu être créé', error.message)
        );
      }
    }
  };

  const classeChamp = (champ: string) => `mt-1 form-input ${erreurs[champ] ? 'border-red-500' : ''}`;
  const erreurDe = (champ: string) =>
    erreurs[champ] && <p className="mt-1 text-sm text-red-600">{erreurs[champ]}</p>;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onMouseDown={(e) => e.target === e.currentTarget && demanderFermeture()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="titre-fenetre-stage"
        className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b">
          <h2 id="titre-fenetre-stage" className="text-xl font-semibold">
            {stage ? 'Modifier le stage' : 'Nouveau stage'}
          </h2>
          <button
            type="button"
            onClick={demanderFermeture}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Fermer"
            disabled={occupe}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={enregistrer} className="flex flex-col min-h-0 flex-1" noValidate>
          {/* Le contenu defile ; l'en-tete et les boutons restent visibles. */}
          <div className="overflow-y-auto px-6 py-4 space-y-4">
            <div>
              <span className="block text-sm font-medium text-gray-700 mb-2">Image</span>
              <div className="flex items-center space-x-4">
                <div className="h-24 w-24 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0">
                  {apercu ? (
                    <img src={apercu} alt="Aperçu de la vignette" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-xs text-gray-400 text-center px-2">
                      Aucune image
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label className={`btn-outline inline-flex items-center ${occupe ? 'opacity-50' : 'cursor-pointer'}`}>
                    <input
                      type="file"
                      accept={ACCEPT_IMAGE}
                      onChange={choisirImage}
                      className="hidden"
                      disabled={occupe}
                    />
                    {preparation ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {preparation ? 'Préparation…' : apercu ? 'Changer l’image' : 'Choisir une image'}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    JPG, PNG ou WebP. Les photos lourdes sont compressées automatiquement.
                  </p>
                  {erreurImage && (
                    <p className="mt-1 text-sm text-red-600" role="alert">
                      {erreurImage}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="stage-titre" className="block text-sm font-medium text-gray-700">
                Titre
              </label>
              <input
                id="stage-titre"
                ref={titreRef}
                type="text"
                value={form.title}
                onChange={(e) => changer('title', e.target.value)}
                className={classeChamp('title')}
              />
              {erreurDe('title')}
            </div>

            {/*
              Le type d'activite. Il est demande a la creation parce que le
              defaut de la base — « stage » — rangerait sinon une nouvelle
              plaine du mauvais cote, en silence. Ce qui se voit sur les pages
              publiques : titres, phrases, JSON-LD et regroupement du
              catalogue en dependent.
            */}
            <div>
              <label htmlFor="stage-type" className="block text-sm font-medium text-gray-700">
                Type d’activité
              </label>
              <select
                id="stage-type"
                value={form.type_activite}
                onChange={(e) => changer('type_activite', e.target.value as TypeDActivite)}
                className="mt-1 form-input"
              >
                {(['plaine', 'stage', 'sejour'] as TypeDActivite[]).map((t) => (
                  <option key={t} value={t}>
                    {NOM_DU_TYPE_PLURIEL[t]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">{CE_QU_EST_LE_TYPE[form.type_activite]}</p>
            </div>

            <div>
              <label htmlFor="stage-description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="stage-description"
                value={form.description}
                onChange={(e) => changer('description', e.target.value)}
                className={classeChamp('description')}
                rows={6}
              />
              <p
                className={`mt-1 text-xs text-right ${
                  form.description.length > LONGUEUR_CONSEILLEE ? 'text-amber-600' : 'text-gray-400'
                }`}
              >
                {form.description.length} caractères
                {form.description.length > LONGUEUR_CONSEILLEE && ' — un texte plus court se lit mieux'}
              </p>
              {erreurDe('description')}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="stage-age-min" className="block text-sm font-medium text-gray-700">
                  Âge minimum
                </label>
                <input
                  id="stage-age-min"
                  type="number"
                  value={form.age_min}
                  onChange={(e) => changer('age_min', e.target.value)}
                  className={classeChamp('age_min')}
                  step="0.5"
                  min="2"
                  max="18"
                />
                {erreurDe('age_min')}
              </div>

              <div>
                <label htmlFor="stage-age-max" className="block text-sm font-medium text-gray-700">
                  Âge maximum (exclu)
                </label>
                <input
                  id="stage-age-max"
                  type="number"
                  value={form.age_max}
                  onChange={(e) => changer('age_max', e.target.value)}
                  className={classeChamp('age_max')}
                  step="0.5"
                  min="2.5"
                  max="19"
                />
                <p className="mt-1 text-xs text-gray-500">L'enfant doit avoir strictement moins que cet âge</p>
                {erreurDe('age_max')}
              </div>
            </div>

            <div>
              <label htmlFor="stage-materiel" className="block text-sm font-medium text-gray-700">
                Matériel spécifique à ce stage
              </label>
              <textarea
                id="stage-materiel"
                value={form.materiel}
                onChange={(e) => changer('materiel', e.target.value)}
                className="mt-1 form-input"
                rows={3}
                placeholder={'Un élément par ligne, par exemple :\nUn vélo adapté à sa taille et en bon état\nUn casque'}
              />
              <p className="mt-1 text-xs text-gray-500">
                Repris dans le courriel envoyé avant le stage, sous chaque enfant inscrit. Le matériel commun à tous les
                stages se gère dans « Matériel à prévoir ».
              </p>
              <label className="mt-2 flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.pret_materiel}
                  onChange={(e) => changer('pret_materiel', e.target.checked)}
                  disabled={!form.materiel.trim()}
                  className="mt-1"
                />
                <span>
                  Éclosion peut prêter ce matériel sur demande
                  <span className="block text-xs text-gray-500">
                    Le courriel invite les parents à prévenir info@eclosion.be s'il leur manque quelque chose.
                  </span>
                </span>
              </label>
            </div>

            <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => changer('active', e.target.checked)}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-medium text-gray-700">Actif</span>
                <span className="block text-xs text-gray-500">
                  Un stage inactif n’est plus proposé dans l’outil de programmation. Ses sessions existantes ne
                  changent pas.
                </span>
              </span>
            </label>

            {erreurEnregistrement && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg flex items-start text-sm" role="alert">
                <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
                <p>{erreurEnregistrement}</p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t">
            {confirmerFermeture ? (
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-gray-700">Les modifications ne sont pas enregistrées.</p>
                <div className="flex gap-2 flex-shrink-0">
                  <button type="button" onClick={() => setConfirmerFermeture(false)} className="btn-outline">
                    Continuer
                  </button>
                  <button type="button" onClick={onClose} className="btn-primary bg-red-600 hover:bg-red-700">
                    Abandonner
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-end space-x-3">
                <button type="button" onClick={demanderFermeture} className="btn-outline" disabled={occupe}>
                  Annuler
                </button>
                <button type="submit" className="btn-primary" disabled={occupe}>
                  {occupe ? (
                    <span className="flex items-center">
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      {preparation
                        ? 'Préparation de la photo…'
                        : imageFile
                          ? 'Envoi de la photo et enregistrement…'
                          : 'Enregistrement…'}
                    </span>
                  ) : stage ? (
                    'Enregistrer'
                  ) : (
                    'Créer le stage'
                  )}
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default StageFormModal;
